import {
    App,
    CommsCampaign,
    CommsCampaignEmailAttachment,
    CommsCampaignMessage,
    CommsCampaignStage,
    CommsCampaignsState,
    HealthcareProvider,
    HealthcareProvidersState,
    DashboardRole,
    Nullable,
    User,
} from '../interfaces';
import { PresentedData, PresentedView, Presenter } from './Presentation';
import { deleteObject, getBlob, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { hasDashboardFeature, isRootOrSuperRole } from '../DashboardAccess';

type PresentedMessage = CommsCampaignMessage & {
    id: string;
};

type MessageEditorSubmission = {
    message: CommsCampaignMessage;
    attachmentFile?: File | null;
    removeAttachment?: boolean;
};

// Generate a stable id once when a dashboard user creates a new message step.
function createCampaignMessageId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `msg-${crypto.randomUUID()}`;
    }

    return `msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function findMessageIndexById(messages: Array<CommsCampaignMessage>, messageId: string): number {
    return messages.findIndex((message) => message.id === messageId);
}

// Keep attachment object names filesystem-safe while preserving a readable filename.
function sanitizeAttachmentFileName(fileName: string): string {
    return fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export class MessagesPresenter implements Presenter {
    private app: App;
    private view: Nullable<PresentedView>;
    private healthcareProviderId!: string;
    private campaigns: Array<CommsCampaign> = [];
    private healthcareProviders: Array<HealthcareProvider> = [];
    private isSuper: boolean = false;
    private dashboardRole: DashboardRole = 'unauthenticated';

    constructor(options: { app: App, healthcareProviderId: string }) {
        const { app, healthcareProviderId } = options;
        if (!app) {
            throw new Error('MessagesPresenter(): missing app parameter');
        }
        this.app = app;
        this.healthcareProviderId = healthcareProviderId;
        this.observeAuthenticatedUser();
        this.observeHealthcareProviders();
        this.observeCommsCampaigns();
    }

    setHealthcareProviderId(healthcareProviderId: string) {
        this.healthcareProviderId = healthcareProviderId;
        this.safeUpdatePresentedData();
    }

    // Track the authenticated user role so the copy action only appears for superusers.
    private observeAuthenticatedUser() {
        this.app.queryUser().observe({
            changed: (user: User) => {
                this.dashboardRole = user.dashboardRole;
                this.isSuper = isRootOrSuperRole(this.dashboardRole);
                this.safeUpdatePresentedData();
            }
        });
    }

    // Keep the healthcare provider picker options aligned with the dashboard header.
    private observeHealthcareProviders() {
        this.app.getData().queryHealthcareProviders().observe({
            changed: (healthcareProvidersState: HealthcareProvidersState) => {
                this.healthcareProviders = healthcareProvidersState.healthcareProviders;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeCommsCampaigns() {
        this.app.getData().queryCommsCampaigns().observe({
            changed: (campaignsState: CommsCampaignsState) => {
                this.campaigns = campaignsState.campaigns;
                this.safeUpdatePresentedData();
            }
        });
    }

    private getPrimaryCampaign(): Nullable<CommsCampaign> {
        return this.campaigns.length > 0 ? this.campaigns[0] : null;
    }

    private requireMessagingAccess() {
        // Keep write callbacks protected even if a stale view remains mounted.
        const provider = this.healthcareProviders.find((candidateProvider) =>
            candidateProvider.id === this.healthcareProviderId
        );
        if (!hasDashboardFeature(provider, this.dashboardRole, 'messaging')) {
            throw new Error('Your role does not have access to Messaging.');
        }
    }

    // Store campaign email attachments in Firebase Storage under the provider/campaign path.
    private async uploadEmailAttachment(
        campaign: CommsCampaign,
        file: File
    ): Promise<CommsCampaignEmailAttachment> {
        const fileId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        const safeOriginalName = sanitizeAttachmentFileName(file.name);
        const storagePath = `HealthcareProviders/${campaign.healthcareProvider}/CommsCampaigns/${campaign.id}/attachments/${fileId}-${safeOriginalName}`;

        await uploadBytes(ref(storage, storagePath), file, {
            contentType: file.type || 'application/octet-stream',
        });

        return {
            fileId,
            originalName: file.name,
            storagePath,
            contentType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
        };
    }

    // Attachment cleanup is best-effort so message edits are not blocked by orphan cleanup.
    private async deleteEmailAttachment(attachment?: CommsCampaignEmailAttachment) {
        if (!attachment?.storagePath) {
            return;
        }

        try {
            await deleteObject(ref(storage, attachment.storagePath));
        } catch (error) {
            console.error('MESSAGES_PRESENTER: deleteEmailAttachment(): Error deleting attachment:', error);
        }
    }

    // Check ownership before cleaning up attachments after a campaign replacement.
    private attachmentBelongsToCampaign(
        attachment: CommsCampaignEmailAttachment,
        campaign: CommsCampaign
    ): boolean {
        const campaignAttachmentPrefix = `HealthcareProviders/${campaign.healthcareProvider}/CommsCampaigns/${campaign.id}/attachments/`;
        return attachment.storagePath.startsWith(campaignAttachmentPrefix);
    }

    // List unique attachments that can be safely deleted from the selected campaign.
    private getOwnedCampaignAttachments(campaign: CommsCampaign): Array<CommsCampaignEmailAttachment> {
        const attachmentsByStoragePath = new Map<string, CommsCampaignEmailAttachment>();

        (campaign.messages || []).forEach((message) => {
            const attachment = message.emailAttachment;
            if (attachment && this.attachmentBelongsToCampaign(attachment, campaign)) {
                attachmentsByStoragePath.set(attachment.storagePath, attachment);
            }
        });

        return Array.from(attachmentsByStoragePath.values());
    }

    // Copy an email attachment into the target provider/campaign storage location.
    private async copyEmailAttachment(
        sourceAttachment: CommsCampaignEmailAttachment,
        targetCampaign: CommsCampaign
    ): Promise<CommsCampaignEmailAttachment> {
        const fileId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        const originalName = sourceAttachment.originalName || 'attachment';
        const safeOriginalName = sanitizeAttachmentFileName(originalName);
        const storagePath = `HealthcareProviders/${targetCampaign.healthcareProvider}/CommsCampaigns/${targetCampaign.id}/attachments/${fileId}-${safeOriginalName}`;
        const sourceBlob = await getBlob(ref(storage, sourceAttachment.storagePath));
        const contentType = sourceAttachment.contentType || sourceBlob.type || 'application/octet-stream';

        await uploadBytes(ref(storage, storagePath), sourceBlob, {
            contentType,
        });

        return {
            fileId,
            originalName,
            storagePath,
            contentType,
            sizeBytes: sourceAttachment.sizeBytes || sourceBlob.size,
        };
    }

    // Normalize source stages while cloning messages from another provider.
    private getMessageStages(message: CommsCampaignMessage): Array<CommsCampaignStage> {
        if (Array.isArray(message.stages) && message.stages.length > 0) {
            return message.stages;
        }

        return message.stage ? [message.stage] : ['referral_created_not_signed_in'];
    }

    // Create a target campaign message with a fresh message id and copied attachment.
    private async copyMessageForCampaign(
        message: CommsCampaignMessage,
        targetCampaign: CommsCampaign
    ): Promise<CommsCampaignMessage> {
        const {
            id: _sourceMessageId,
            stage: _legacyStage,
            emailAttachment,
            channels,
            stages: _sourceStages,
            ...messageWithoutIdentity
        } = message;
        const copiedAttachment = emailAttachment
            ? await this.copyEmailAttachment(emailAttachment, targetCampaign)
            : undefined;

        return {
            ...messageWithoutIdentity,
            id: createCampaignMessageId(),
            stages: [...this.getMessageStages(message)],
            channels: [...channels],
            ...(copiedAttachment ? { emailAttachment: copiedAttachment } : {}),
        };
    }

    private getMessages(): Array<PresentedMessage> {
        return (this.getPrimaryCampaign()?.messages || []).flatMap((message) => {
            if (!message.id) {
                console.warn('MESSAGES_PRESENTER: Skipping campaign message without a stable id.');
                return [];
            }

            return [{
                ...message,
                id: message.id,
            }];
        });
    }

    private async createMessage(submission: MessageEditorSubmission) {
        this.requireMessagingAccess();
        const selectedCampaign = this.getPrimaryCampaign();
        if (!selectedCampaign) {
            throw new Error('No campaign is configured for this healthcare provider.');
        }

        let uploadedAttachment: CommsCampaignEmailAttachment | undefined;
        if (submission.attachmentFile) {
            uploadedAttachment = await this.uploadEmailAttachment(selectedCampaign, submission.attachmentFile);
        }

        const updatedCampaign: CommsCampaign = {
            ...selectedCampaign,
            messages: [...(selectedCampaign.messages || []), {
                ...submission.message,
                id: submission.message.id || createCampaignMessageId(),
                ...(uploadedAttachment ? { emailAttachment: uploadedAttachment } : {}),
            }],
        };

        try {
            await this.app.getData().updateCommsCampaign(updatedCampaign);
        } catch (error) {
            await this.deleteEmailAttachment(uploadedAttachment);
            throw error;
        }
    }

    private async deleteMessage(messageId: string) {
        this.requireMessagingAccess();
        const selectedCampaign = this.getPrimaryCampaign();
        if (!selectedCampaign) {
            throw new Error('No campaign is configured for this healthcare provider.');
        }

        const messageIndex = findMessageIndexById(selectedCampaign.messages, messageId);
        if (messageIndex < 0 || messageIndex >= selectedCampaign.messages.length) {
            throw new Error('Could not find the selected message to delete.');
        }

        const attachmentToDelete = selectedCampaign.messages[messageIndex]?.emailAttachment;
        const updatedCampaign: CommsCampaign = {
            ...selectedCampaign,
            messages: selectedCampaign.messages.filter((_, index) => index !== messageIndex),
        };

        await this.app.getData().updateCommsCampaign(updatedCampaign);
        await this.deleteEmailAttachment(attachmentToDelete);
    }

    private async updateMessage(messageId: string, submission: MessageEditorSubmission) {
        this.requireMessagingAccess();
        const selectedCampaign = this.getPrimaryCampaign();
        if (!selectedCampaign) {
            throw new Error('No campaign is configured for this healthcare provider.');
        }

        const messageIndex = findMessageIndexById(selectedCampaign.messages, messageId);
        if (messageIndex < 0 || messageIndex >= selectedCampaign.messages.length) {
            throw new Error('Could not find the selected message to update.');
        }

        const existingMessage = selectedCampaign.messages[messageIndex];
        let uploadedAttachment: CommsCampaignEmailAttachment | undefined;
        if (submission.attachmentFile) {
            uploadedAttachment = await this.uploadEmailAttachment(selectedCampaign, submission.attachmentFile);
        }

        const nextAttachment = uploadedAttachment ||
            (submission.removeAttachment ? undefined : submission.message.emailAttachment);
        const updatedCampaign: CommsCampaign = {
            ...selectedCampaign,
            messages: selectedCampaign.messages.map((existingMessage, index) =>
                index === messageIndex ? {
                    ...submission.message,
                    id: existingMessage.id || submission.message.id || createCampaignMessageId(),
                    ...(nextAttachment ? { emailAttachment: nextAttachment } : {}),
                } : existingMessage
            ),
        };

        try {
            await this.app.getData().updateCommsCampaign(updatedCampaign);
            const replacedOrRemovedAttachment = existingMessage.emailAttachment &&
                existingMessage.emailAttachment.storagePath !== nextAttachment?.storagePath;
            if (replacedOrRemovedAttachment) {
                await this.deleteEmailAttachment(existingMessage.emailAttachment);
            }
        } catch (error) {
            await this.deleteEmailAttachment(uploadedAttachment);
            throw error;
        }
    }

    // Replace the selected provider's messages with cloned messages from another provider.
    private async copyMessagesFromHealthcareProvider(sourceHealthcareProviderId: string): Promise<number> {
        this.requireMessagingAccess();
        if (!this.isSuper) {
            throw new Error('Only Root or Super users can copy messages between healthcare providers.');
        }

        const selectedCampaign = this.getPrimaryCampaign();
        if (!selectedCampaign) {
            throw new Error('No campaign is configured for this healthcare provider.');
        }

        if (!sourceHealthcareProviderId || sourceHealthcareProviderId === this.healthcareProviderId) {
            throw new Error('Select another healthcare provider to copy from.');
        }

        const sourceCampaigns = await this.app.getData().fetchCommsCampaignsForHealthcareProvider(sourceHealthcareProviderId);
        const sourceCampaign = sourceCampaigns.length > 0 ? sourceCampaigns[0] : null;
        if (!sourceCampaign) {
            throw new Error('No campaign is configured for the selected healthcare provider.');
        }

        const copiedAttachments: Array<CommsCampaignEmailAttachment> = [];
        try {
            // Copy attachments before saving so the campaign never points at missing files.
            const copiedMessages: Array<CommsCampaignMessage> = [];
            for (const message of sourceCampaign.messages || []) {
                const copiedMessage = await this.copyMessageForCampaign(message, selectedCampaign);
                if (copiedMessage.emailAttachment) {
                    copiedAttachments.push(copiedMessage.emailAttachment);
                }
                copiedMessages.push(copiedMessage);
            }

            const oldAttachments = this.getOwnedCampaignAttachments(selectedCampaign);
            const updatedCampaign: CommsCampaign = {
                ...selectedCampaign,
                messages: copiedMessages,
            };

            await this.app.getData().updateCommsCampaign(updatedCampaign);
            await Promise.all(oldAttachments.map((attachment) => this.deleteEmailAttachment(attachment)));

            return copiedMessages.length;
        } catch (error) {
            // Roll back any newly uploaded files if the campaign replacement fails.
            await Promise.all(copiedAttachments.map((attachment) => this.deleteEmailAttachment(attachment)));
            throw error;
        }
    }

    presentedData(): PresentedData {
        const accessibleSourceProviders = this.isSuper ? this.healthcareProviders.filter((provider) =>
            provider.id !== this.healthcareProviderId &&
            hasDashboardFeature(provider, this.dashboardRole, 'messaging')
        ) : [];
        return {
            messages: this.getMessages(),
            healthcareProviderId: this.healthcareProviderId,
            healthcareProviders: accessibleSourceProviders,
            isSuper: this.isSuper,
            canCreateMessage: this.campaigns.length > 0,
            onCreateMessage: async (submission: MessageEditorSubmission) => this.createMessage(submission),
            onDeleteMessage: async (messageId: string) => this.deleteMessage(messageId),
            onUpdateMessage: async (messageId: string, submission: MessageEditorSubmission) => this.updateMessage(messageId, submission),
            onCopyMessagesFromHealthcareProvider: async (healthcareProviderId: string) => this.copyMessagesFromHealthcareProvider(healthcareProviderId),
        };
    }

    viewWillUpdate(view: PresentedView): void {
        this.view = view;
    }

    viewWillNotUpdate(/*view: PresentedView*/) {
        this.view = null;
    }

    private safeUpdatePresentedData() {
        const data = this.presentedData();
        if (this.view) {
            this.view.updatePresentedData(data);
        }
    }
}
